import { useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchAssistantTasks } from '../services/tasks'
import type { Task } from '../schemas/task'

export const ASSISTANT_TASKS_QUERY_KEY = ['assistant', 'tasks'] as const

export type AssistantSummary = {
  urgent: number
  week: number
  waiting: number
  total: number
  newToday: number
}

const isToday = (iso: string): boolean => {
  const d = new Date(iso)
  const now = new Date()
  return (
    d.getUTCFullYear() === now.getUTCFullYear() &&
    d.getUTCMonth() === now.getUTCMonth() &&
    d.getUTCDate() === now.getUTCDate()
  )
}

export const useAssistantTasks = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ASSISTANT_TASKS_QUERY_KEY,
    queryFn: fetchAssistantTasks,
    staleTime: 30_000,
  })

  const tasks = useMemo<Task[]>(() => data ?? [], [data])

  const summary = useMemo<AssistantSummary>(() => {
    // Counts are per distinct project, not per task. Three urgent tasks on
    // the same project contribute 1 to `urgent`. Tasks without a projectId
    // are bucketed under a single implicit "sans dossier" key so they still
    // count as at most 1.
    const keyFor = (t: Task): string =>
      t.projectId != null ? `#${t.projectId}` : '__none'
    const urgent = new Set<string>()
    const week = new Set<string>()
    const waiting = new Set<string>()
    const totalProjects = new Set<string>()
    const newToday = new Set<string>()
    for (const t of tasks) {
      const key = keyFor(t)
      totalProjects.add(key)
      if (t.bucket === 'urgent') urgent.add(key)
      if (t.bucket === 'week') week.add(key)
      // FR-045: "En attente" merges the two furthest buckets. Anything at
      // least a week out is counted here.
      if (t.bucket === 'later' || t.bucket === 'waiting') waiting.add(key)
      if (isToday(t.createdAt)) newToday.add(key)
    }
    return {
      urgent: urgent.size,
      week: week.size,
      waiting: waiting.size,
      total: totalProjects.size,
      newToday: newToday.size,
    }
  }, [tasks])

  return {
    tasks,
    summary,
    isLoading,
    error: error ? (error instanceof Error ? error.message : String(error)) : null,
  }
}

export const useInvalidateAssistantTasks = () => {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ASSISTANT_TASKS_QUERY_KEY })
}
